import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  Alert
} from '@mui/material';
import MetaAuth from './MetaAuth';
import MetaPageSelector from './MetaPageSelector';
import MetaFormSelector from './MetaFormSelector';
import MetaTrackingConfig from './MetaTrackingConfig';

const steps = [
  'Autenticação',
  'Página',
  'Formulário',
  'Rastreamento'
];

const MetaSetupWizard = ({ onRegistrationSuccess, onCancel, existingData, campanha, setMetaData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [authData, setAuthData] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [selectedForms, setSelectedForms] = useState([]);
  const [trackingConfig, setTrackingConfig] = useState(null);
  const [error, setError] = useState('');

  const handleNext = () => {
    setError('');

    // Validation for each step
    if (activeStep === 0 && !authData) {
      setError('Por favor, complete a autenticação primeiro.');
      return;
    }
    if (activeStep === 1 && selectedPages.length === 0) {
      setError('Por favor, selecione pelo menos uma página.');
      return;
    }
    if (activeStep === 2 && selectedForms.length === 0) {
      setError('Por favor, selecione pelo menos um formulário.');
      return;
    }
    if (activeStep === 3 && !trackingConfig) {
      setError('Por favor, configure as configurações de rastreamento.');
      return;
    }

    if (activeStep === steps.length - 1) {
      // Complete setup
      handleComplete();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = () => {
    const setupData = {
      authData,
      selectedPages,
      selectedForms,
      trackingConfig
    };    
    onRegistrationSuccess(setupData);
  };

  const handleAuthSuccess = (data) => {
    setAuthData(data);
    setActiveStep((prevStep) => prevStep + 1);
    setError('');
  };

  const handlePagesSelected = (pages) => {
    setSelectedPages(pages);
    setError('');
  };

  const handleFormsSelected = (forms) => {
    setSelectedForms(forms);
    setError('');
  };

  const handleTrackingConfigChange = (config) => {
    setTrackingConfig(config);
    const configData = {
      contaId: campanha?.idconta || '',
      campanhaId: campanha?.idcampanha || '',
      track: campanha?.link || '',
      appId: authData.appId || '',
      pixelId: config.pixelId || '',
      accessToken: selectedPages[0].tokenKey || '',
      userAccessToken: authData.tokenKey || '',
      campanha: campanha?.nome || '',
      formId: selectedForms[0].id || '',
      form: selectedForms[0].name || '',
      pageId: selectedPages[0].id || '',
      page: selectedPages[0].name || '',
      track: campanha.link,
      tokenApiConversion: config.tokenApiConversion || ''
    };
    setMetaData(configData);
    setError('');
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <MetaAuth onAuthSuccess={handleAuthSuccess} />
        );
      case 1:
        return (
          <MetaPageSelector
            authData={authData}
            selectedPages={selectedPages}
            onPagesSelected={handlePagesSelected}
          />
        );
      case 2:
        return (
          <MetaFormSelector
            authData={authData}
            selectedPages={selectedPages}
            onFormsSelected={handleFormsSelected}
          />
        );
      case 3:
        return (
          <MetaTrackingConfig
            onTrackingConfigChange={handleTrackingConfigChange}
            page={selectedPages[0]}
            form={selectedForms[0]}
          />
        );
      default:
        return 'Etapa desconhecida';
    }
  };

  const isStepDisabled = (step) => {
    switch (step) {
      case 1:
        return !authData;
      case 2:
        return !authData || selectedPages.length === 0;
      case 3:
        return !authData || selectedPages.length === 0 || selectedForms.length === 0;
      default:
        return false;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!authData;
      case 1:
        return selectedPages.length > 0;
      case 2:
        return selectedForms.length > 0;
      case 3:
        return !!trackingConfig;
      default:
        return false;
    }
  };

  return (
    <Paper sx={{ width: '100%', p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom align="center">
        Configuração da Integração Meta
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 2, mt: 2 }}>
        {steps.map((label, index) => (
          <Step key={label} disabled={isStepDisabled(index)}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ minHeight: 100, mb: 0 }}>
        {getStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button
          color="inherit"
          disabled={activeStep === 0}
          onClick={handleBack}
          sx={{ mr: 1 }}
        >
          Voltar
        </Button>

        <Box>
          {/* <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ mr: 1 }}
          >
            Cancelar
          </Button> */}

          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed()}
          >
            {activeStep === steps.length - 1 ? 'Concluir Configuração' : 'Próximo'}
          </Button>
        </Box>
      </Box>

      {/* Progress Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Progresso da Configuração:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Autenticação: {authData ? '✓ Concluída' : '○ Pendente'}<br />
          • Seleção de Página: {selectedPages.length > 0 ? `✓ ${selectedPages.length} página(s) selecionada(s)` : '○ Pendente'}<br />
          • Seleção de Formulário: {selectedForms.length > 0 ? `✓ ${selectedForms.length} formulário(s) selecionado(s)` : '○ Pendente'}<br />
          • Configuração de Rastreamento: {trackingConfig ? '✓ Concluída' : '○ Pendente'}
        </Typography>
      </Box>
    </Paper>
  );
};

export default MetaSetupWizard;